import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import FadeInUp from "./FadeInUp";

const inputStyle = {
  backgroundColor: "var(--lm-s1)",
  border: "1px solid #23252a",
  color: "var(--lm-ink)",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "14px",
  width: "100%",
  outline: "none",
  transition: "border-color 0.15s",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: "500",
  marginBottom: "6px",
  color: "var(--lm-ink-2)",
};

const LandingContactForm = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const t = (obj) => obj[currentLang] || obj.fr;

  const [contactForm, setContactForm] = useState({
    name: "", email: "", phone: "", company: "",
    companySize: "", industry: "", message: "", gdprConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: "Nouveau contact — Landing Mawered",
          from_name: contactForm.name,
          email: contactForm.email, // reply-to
          phone: contactForm.phone,
          company: contactForm.company,
          company_size: contactForm.companySize,
          industry: contactForm.industry,
          message: contactForm.message,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "submit failed");
      toast.success(
        currentLang === "fr"
          ? "Message envoyé avec succès ! Nous vous recontacterons sous 24h."
          : currentLang === "en"
            ? "Message sent successfully! We'll contact you within 24h."
            : "تم إرسال الرسالة بنجاح! سنتصل بك خلال 24 ساعة.",
      );
      setContactForm({ name: "", email: "", phone: "", company: "", companySize: "", industry: "", message: "", gdprConsent: false });
    } catch {
      toast.error(
        currentLang === "fr"
          ? "Erreur lors de l'envoi. Réessayez ou écrivez à support@mawered.com."
          : currentLang === "en"
            ? "Error sending. Please retry or email support@mawered.com."
            : "حدث خطأ. أعد المحاولة أو راسلنا على support@mawered.com.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field) => (e) => setContactForm({ ...contactForm, [field]: e.target.value });

  const focusStyle = (e) => (e.target.style.borderColor = "#5e6ad2");
  const blurStyle = (e) => (e.target.style.borderColor = "var(--lm-border)");

  return (
    <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: "var(--lm-s1)" }}>
      <div className="max-w-5xl mx-auto">
        <FadeInUp>
          <div className="text-center mb-10 space-y-4">
            <span
              className="inline-block text-xs font-medium uppercase tracking-widest px-3 py-1 rounded-full"
              style={{ color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.1)", border: "1px solid rgba(94,106,210,0.2)" }}
            >
              Contact
            </span>
            <h2
              className="font-semibold"
              style={{ color: "var(--lm-ink)", fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-1px" }}
            >
              {t({ fr: "Contactez-Nous", en: "Contact Us", ar: "اتصل بنا" })}
            </h2>
            <p className="text-base" style={{ color: "var(--lm-ink-2)" }}>
              {t({ fr: "Notre équipe vous répond sous 24h", en: "Our team responds within 24h", ar: "يرد فريقنا خلال 24 ساعة" })}
            </p>
          </div>
        </FadeInUp>

        {/* Book a Demo strip */}
        <FadeInUp delay={0.05}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "32px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "16px",
                background: "rgba(94,106,210,0.08)",
                border: "1px solid rgba(94,106,210,0.2)",
                borderRadius: "12px",
                padding: "12px 20px",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <div>
                <div style={{ color: "var(--lm-ink)", fontSize: "14px", fontWeight: 500 }}>
                  {t({ fr: "Préférez une démo ? Réservez un appel de 30 min", en: "Prefer a demo? Book a 30-min call", ar: "تفضل عرضًا؟ احجز مكالمة 30 دقيقة" })}
                </div>
                <div style={{ color: "#8a8f98", fontSize: "12px" }}>
                  {t({ fr: "Avec un expert Mawered · Tunisie, Qatar ou France", en: "With a Mawered expert · Tunisia, Qatar or France", ar: "مع خبير Mawered · تونس أو قطر أو فرنسا" })}
                </div>
              </div>
              <button
                style={{
                  backgroundColor: "#5e6ad2",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 500,
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#828fff")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                {t({ fr: "Réserver →", en: "Book now →", ar: "احجز →" })}
              </button>
            </div>
          </div>
        </FadeInUp>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Form */}
          <FadeInUp delay={0.1}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label style={labelStyle}>{t({ fr: "Nom complet", en: "Full name", ar: "الاسم الكامل" })} *</label>
                <input
                  required
                  value={contactForm.name}
                  onChange={update("name")}
                  style={inputStyle}
                  onFocus={focusStyle}
                  onBlur={blurStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>{t({ fr: "Email professionnel", en: "Professional email", ar: "البريد الإلكتروني" })} *</label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={update("email")}
                  style={inputStyle}
                  onFocus={focusStyle}
                  onBlur={blurStyle}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>{t({ fr: "Téléphone", en: "Phone", ar: "الهاتف" })}</label>
                  <input
                    value={contactForm.phone}
                    onChange={update("phone")}
                    style={inputStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t({ fr: "Entreprise", en: "Company", ar: "الشركة" })}</label>
                  <input
                    value={contactForm.company}
                    onChange={update("company")}
                    style={inputStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>{t({ fr: "Taille de l'entreprise", en: "Company size", ar: "حجم الشركة" })} *</label>
                  <select
                    required
                    value={contactForm.companySize}
                    onChange={update("companySize")}
                    style={inputStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  >
                    <option value="">{t({ fr: "Sélectionnez...", en: "Select...", ar: "اختر..." })}</option>
                    {["1-10", "11-50", "51-200", "201-500", "500+"].map((size) => (
                      <option key={size} value={size}>{size} {t({ fr: "employés", en: "employees", ar: "موظفين" })}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{t({ fr: "Secteur d'activité", en: "Industry", ar: "القطاع" })} *</label>
                  <select
                    required
                    value={contactForm.industry}
                    onChange={update("industry")}
                    style={inputStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  >
                    <option value="">{t({ fr: "Sélectionnez...", en: "Select...", ar: "اختر..." })}</option>
                    <option value="tech">{t({ fr: "Tech & IT", en: "Tech & IT", ar: "التكنولوجيا" })}</option>
                    <option value="manufacturing">{t({ fr: "Manufacturing", en: "Manufacturing", ar: "التصنيع" })}</option>
                    <option value="retail">{t({ fr: "Retail & Commerce", en: "Retail & Commerce", ar: "التجزئة" })}</option>
                    <option value="services">{t({ fr: "Services & Consulting", en: "Services & Consulting", ar: "الخدمات" })}</option>
                    <option value="healthcare">{t({ fr: "Santé", en: "Healthcare", ar: "الرعاية الصحية" })}</option>
                    <option value="education">{t({ fr: "Éducation", en: "Education", ar: "التعليم" })}</option>
                    <option value="other">{t({ fr: "Autre", en: "Other", ar: "أخرى" })}</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t({ fr: "Message", en: "Message", ar: "الرسالة" })} *</label>
                <textarea
                  required
                  rows={5}
                  value={contactForm.message}
                  onChange={update("message")}
                  placeholder={t({ fr: "Parlez-nous de votre projet...", en: "Tell us about your project...", ar: "أخبرنا عن مشروعك..." })}
                  style={{ ...inputStyle, resize: "vertical" }}
                  onFocus={focusStyle}
                  onBlur={blurStyle}
                />
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="gdprConsent"
                  required
                  checked={contactForm.gdprConsent}
                  onChange={(e) => setContactForm({ ...contactForm, gdprConsent: e.target.checked })}
                  className="mt-0.5"
                  style={{ accentColor: "#5e6ad2" }}
                />
                <label htmlFor="gdprConsent" className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
                  {t({ fr: "J'accepte que mes données soient traitées conformément à la ", en: "I agree that my data will be processed in accordance with the ", ar: "أوافق على معالجة بياناتي وفقًا لـ " })}
                  <Link to="/privacy" style={{ color: "#5e6ad2" }}>
                    {t({ fr: "Politique de Confidentialité", en: "Privacy Policy", ar: "سياسة الخصوصية" })}
                  </Link>{" "}*
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting || !contactForm.gdprConsent}
                className="w-full py-2.5 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors"
                style={{
                  backgroundColor: submitting || !contactForm.gdprConsent ? "var(--lm-border)" : "#5e6ad2",
                  color: submitting || !contactForm.gdprConsent ? "var(--lm-ink-3)" : "#ffffff",
                  cursor: submitting || !contactForm.gdprConsent ? "not-allowed" : "pointer",
                }}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    {t({ fr: "Envoi...", en: "Sending...", ar: "جارٍ الإرسال..." })}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    {t({ fr: "Envoyer le message", en: "Send message", ar: "إرسال الرسالة" })}
                  </>
                )}
              </button>
            </form>
          </FadeInUp>

          {/* Contact info */}
          <FadeInUp delay={0.2}>
            <div className="space-y-4">
              {[
                {
                  icon: MapPin,
                  title: { fr: "Tunisie", en: "Tunisia", ar: "تونس" },
                  content: "Avenue 20 Mars, Jawahra Sousse\nTunisie",
                },
                {
                  icon: MapPin,
                  title: { fr: "Qatar", en: "Qatar", ar: "قطر" },
                  content: "QFC Business Centre, Office No. 4\nQFC Tower 1, West Bay, Doha",
                },
                {
                  icon: Mail,
                  title: { fr: "Email", en: "Email", ar: "البريد الإلكتروني" },
                  content: "contact@mawered.com",
                },
                {
                  icon: Phone,
                  title: { fr: "Téléphone", en: "Phone", ar: "الهاتف" },
                  content: "+216 56 68 74 15\n+216 53 40 35 22",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl flex gap-4"
                  style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "var(--lm-s3)", border: "1px solid #23252a" }}
                  >
                    <item.icon className="w-4 h-4" style={{ color: "#5e6ad2" }} />
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t(item.title)}</p>
                    {item.content.split("\n").map((line, li) => (
                      <p key={li} className="text-sm" dir="ltr" style={{ color: "#d0d6e0" }}>{line}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* International support */}
              <div
                className="p-5 rounded-xl"
                style={{ backgroundColor: "rgba(94,106,210,0.08)", border: "1px solid rgba(94,106,210,0.2)" }}
              >
                <p className="text-sm font-medium mb-2" style={{ color: "#828fff" }}>
                  {t({ fr: "Support international", en: "International support", ar: "دعم دولي" })}
                </p>
                <p className="text-xs leading-relaxed" style={{ color: "var(--lm-ink-2)" }}>
                  {t({
                    fr: "Présents sur plusieurs continents — EMEA & Golfe. Une équipe disponible quel que soit votre fuseau horaire.",
                    en: "Present across multiple continents — EMEA & Gulf. A team available whatever your time zone.",
                    ar: "حاضرون عبر عدة قارات — EMEA والخليج. فريق متاح مهما كانت منطقتك الزمنية.",
                  })}
                </p>

                {/* Response time badge */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "12px" }}>
                  <div style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    background: "#27a644", boxShadow: "0 0 6px #27a644",
                  }} />
                  <span style={{ color: "#62666d", fontSize: "12px" }}>
                    {t({ fr: "Réponse sous 24h · Support FR/EN/AR", en: "Response within 24h · FR/EN/AR support", ar: "رد خلال 24 ساعة · دعم FR/EN/AR" })}
                  </span>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </div>
    </section>
  );
};

export default LandingContactForm;
